import React, { useState, useEffect, useContext } from "react";
import moment from "moment";
import Joi from "joi";
import { Modal } from "react-bootstrap";
import Input from "./common/input";
import Select from "./common/select";
import http from "../services/http";
import { toast } from "react-toastify";
import UserContext from "../context/userContext";
import { useCallback } from "react";
import SelectDate from "./common/selectDate";

const Recalculate = () => {
  const [user] = useContext(UserContext);
  const [operations, setOperations] = useState([]);
  const [active, setActive] = useState({
    code: "",
    date: moment(),
  });
  const [modalData, setModalData] = useState({
    code: "",
    date: moment(),
  });
  // const [focused, setFocused] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [data, setData] = useState([]);

  const Schema = {
    code: Joi.string().required(),
    date: Joi.date().required(),
  };

  useEffect(() => {
    document.title = "FTR - Recalculate";
    return () => {
      document.title = "FTR";
    };
  }, []);

  useEffect(() => {
    async function getOperations() {
      try {
        const { data } = await http.get(`/operations/active`);
        setOperations(data);
      }
      catch {
        //ignore error
      }
    }

    if (user && user.SITE !== "CO") {
      getOperations();
    }
  }, [user]);

  function handleChange({ currentTarget: input }) {
    const newErrors = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) newErrors[input.name] = errorMessage;
    else delete newErrors[input.name];
    setErrors(newErrors);

    const newActive = { ...active };
    newActive[input.name] = input.value;
    setActive(newActive);
  }

  function handleDateChange({ currentTarget: input }) {
    const newActive = { ...active };
    newActive.date = moment(input.value);
    setActive(newActive);
  }

  function handleModalChange({ currentTarget: input }) {
    const newActive = { ...modalData };
    newActive[input.name] = input.value;
    setModalData(newActive);
  }

  function handleModalDateChange({ currentTarget: input }) {
    const newData = { ...modalData };
    newData.date = moment(input.value);
    setModalData(newData);
  }

  function validateProperty({ name, value }) {
    const obj = { [name]: value };
    const propSchema = Joi.object({ [name]: Schema[name] });
    const { error } = propSchema.validate(obj);
    return error ? error.details[0].message : null;
  }

  const handleShowManual = () => {
    setModalData({
      code: active.code,
      date: active.date,
      hour: "",
      operation: "",
    });
    setShowModal(true);
  };

  const getData = useCallback(async () => {
    const date = active.date.format("YYYY-MM-DD");
    const code = active.code.toUpperCase();

    if (code !== "") {
      try {
        const { data } = await http.get(`/data/${user.SITE}/${code}/${date}/${date}`);
        setData(data);
      }
      catch {
        //ignore error
      }
    }
    else {
      toast.error("Code must be entered");
    }
  }, [user, active]);

  async function handleFetch(e) {
    e.preventDefault();

    setLoading(true);
    try {
      await getData();
    } catch (ex) {
      if (ex.response && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    } finally {
      setLoading(false);
      setSearched(true);
    }
  }

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    const realDatetime = moment
      .utc(modalData.date)
      .startOf("day")
      .add(modalData.hour, "hours");
    const ftrDatetime = realDatetime.add(17, "hours");
    let ftrDate = ftrDatetime.format("YYYY-MM-DD");
    let hour = ftrDatetime.hour();
    if (hour === 0) {
      ftrDate = ftrDatetime.subtract(1, "day").format("YYYY-MM-DD");
      hour = 24;
    }

    await handleSubmit({
      PARM_CODE: modalData.code.toUpperCase(),
      PARAM_DATE: ftrDate,
      HOUR: hour,
      OPTN_CODE: modalData.operation,
    });
    setShowModal(false);
  };

  async function handleSubmit(record) {
    const payload = {
      code: record.PARM_CODE,
      date: record.PARAM_DATE,
      hour: record.HOUR,
      operation: record.OPTN_CODE,
    };
    setCalculating(true);
    try {
      await http.post(`/recalculate`, payload);
      await http.post(`/data/recalculate/${record.OPTN_CODE}`);
      await getData();
      toast.success("Recalculated");
    } catch (ex) {
      if (ex.response && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    } finally {
      setCalculating(false);
    }
  }

  return (
    <>
      <div className="col-10 mt-4">
        <form onSubmit={handleFetch} className="container">
          <div className="row">
            <div className="col">
              <Input
                name="code"
                label="Parameter Code"
                placeholder="Code"
                value={active.code}
                onChange={handleChange}
                error={errors.code}
              />
            </div>
            <div className="col">
              <div className="form-group">
                <label>Day Ending</label>
                <SelectDate
                  value={active.date.format("YYYY-MM-DD")}
                  onChange={handleDateChange}
                />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <button className="btn btn-primary" disabled={loading}>
                {loading && (
                  <div
                    className="spinner-border spinner-border-sm text-white mr-1"
                    role="status"
                  ></div>
                )}
                Get Data
              </button>
            </div>
          </div>
        </form>
        <div className="row my-3">
          {searched && (
            <div className="container d-flex align-items-center flex-row">
              <span>Don't see the record you're after?</span>
              <button
                className="btn btn-sm btn-outline-success ml-3"
                onClick={handleShowManual}
              >
                Manual
              </button>
            </div>
          )}
        </div>
        <div className="row">
          <div className="container">
            <table className="table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Operation</th>
                  <th>Value</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.map((data) => (
                  <tr key={`${data.PARAM_DATE}${data.HOUR}${data.OPTN_CODE}`}>
                    <td>
                      {moment(data.REAL_DATETIME).format("ddd Do MMM hh:mma")}
                    </td>
                    <td>{data.OPTN_CODE}</td>
                    <td>{data.PARAM_VALUE}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleSubmit(data)}
                        disabled={calculating}
                      >
                        Recalculate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <ManualModal
        show={showModal}
        data={modalData}
        operations={operations}
        onChange={handleModalChange}
        onDateChange={handleModalDateChange}
        onSubmit={handleModalSubmit}
        onHide={() => setShowModal(false)}
        calculating={calculating}
      />
    </>
  );
};

const ManualModal = ({
  show,
  data,
  operations,
  onChange,
  onDateChange,
  onSubmit,
  onHide,
  calculating,
}) => {
  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton className="bg-success">
        <Modal.Title>Manual Recalculation</Modal.Title>
      </Modal.Header>
        <form onSubmit={onSubmit}>
        <Modal.Body>
          <h5 className="modalWrap">Please note:</h5>
          <h6>Dates and hours are in realtime, daily values are Hour 7.</h6>
          <Input
            name="code"
            label="Parameter Code"
            placeholder="Code"
            value={data.code}
            onChange={onChange}
          />
          <div className="form-group">
            <label>Date</label>
            <SelectDate
              value={data.date.format("YYYY-MM-DD")}
              onChange={onDateChange}
            />
          </div>
          <Input
            name="hour"
            label="Hour"
            placeholder="1 - 24"
            value={data.hour}
            onChange={onChange}
          />
          <Select
            name="operation"
            label="Operation"
            placeholder="Operation"
            value={data.operation}
            options={operations}
            onChange={onChange}
            listKey="CODE"
            listName="COMMENTS"
          />
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-primary" disabled={calculating}>
            Recalculate
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default Recalculate;
